/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState, useImperativeHandle, forwardRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { selectCurrentUserApiToken } from "../../slices/currentUser/currentUserSlice";
import { m11ResearchStudyResourceChanged, selectM11ResearchStudyResource } from "../../slices/m11ReportAuthoringTool/m11ReportAuthoringToolSlice";
import jsonEquals from "../../functions/jsonEquals";
import StringEntry from "../../datatypeComponents/StringEntry";
import ArrayEntry from "../../datatypeComponents/ArrayEntry";
import saveM11Changes from "../../functions/saveM11ChangesAPI";
import { selectM11ResearchStudyFoi } from "../../slices/m11StepCount/m11StepCountSlice";
import { validateStep } from "../../functions/validateM11ReportAPI";

const saveIdentifierChanges = async (dispatch, apiToken, m11ResearchStudyFoi,
    identifiersState, setAlertModalState, setStartingIdentifiersState) => {
    let changesStates = {
        identifiersState: identifiersState
    };
    let response = await saveM11Changes(apiToken, 2, m11ResearchStudyFoi, changesStates);
    if (response?.success) {
        if (response.m11ResearchStudyResource) {
            dispatch(m11ResearchStudyResourceChanged(response.m11ResearchStudyResource));
        }
        setStartingIdentifiersState(JSON.parse(JSON.stringify(identifiersState)));
    } else {
        if (response.error) {
            alert(response.error);
        }
    }
};

const M11Step002Identifiers = forwardRef(({ theM11ResearchStudyFoi, setAlertModalState }, ref) => {

    const dispatch = useDispatch();
    const apiToken = useSelector(selectCurrentUserApiToken);
    const m11ResearchStudyResource = useSelector(selectM11ResearchStudyResource);
    const m11ResearchStudyFoi = useSelector(selectM11ResearchStudyFoi);

    const [identifiersState, setIdentifiersState] = useState({
        "sponsorid": "", "sponsorname": "",
        "euCTnumber": "", "fdaINDnumber": "", "ideNumber": "",
        "jRCTnumber": "", "nctid": "", "nmpaINDnumber": "",
        "whoUTNnumber": "", "otherIdentifiers": []
    });
    const [startingIdentifiersState, setStartingIdentifiersState] = useState({
        "sponsorid": "", "sponsorname": "",
        "euCTnumber": "", "fdaINDnumber": "", "ideNumber": "",
        "jRCTnumber": "", "nctid": "", "nmpaINDnumber": "",
        "whoUTNnumber": "", "otherIdentifiers": []
    });

    useImperativeHandle(ref, () => ({
        unsavedChanges: (m11ResearchStudyResource && !jsonEquals(identifiersState, startingIdentifiersState)),
        async saveAllChanges() {
            await saveIdentifierChanges(dispatch, apiToken, m11ResearchStudyFoi,
                identifiersState, setAlertModalState, setStartingIdentifiersState);
        }
    }));

    useEffect(() => {
        if (m11ResearchStudyResource) {
            let startingIdentifierSet = {
                "sponsorid": "", "sponsorname": "",
                "euCTnumber": "", "fdaINDnumber": "", "ideNumber": "",
                "jRCTnumber": "", "nctid": "", "nmpaINDnumber": "",
                "whoUTNnumber": "", "otherIdentifiers": []
            };
            if (m11ResearchStudyResource.associatedParty) {
                for (const associated of m11ResearchStudyResource.associatedParty) {
                    if (associated.role?.coding?.[0]?.code === "lead-sponsor") {
                        startingIdentifierSet.sponsorname = associated.name;
                        break;
                    } else if (associated.role?.coding?.[0]?.code === "sponsor" &&
                        !(associated.role.text?.toLowerCase() === "co-sponsor" ||
                            associated.role.text?.toLowerCase() === "cosponsor" ||
                            associated.role.text?.toLowerCase() === "local sponsor")) {
                        startingIdentifierSet.sponsorname = associated.name;
                        break;
                    }
                }
            }
            if (m11ResearchStudyResource.identifier) {
                for (const identifier of m11ResearchStudyResource.identifier) {
                    if (identifier.system === "https://fevir.net/FOI") {
                        //
                    } else if (identifier.system === "https://clinicaltrials.gov/nctid" || identifier.type?.coding?.[0]?.code === "C172240") {
                        startingIdentifierSet.nctid = identifier.value;
                    } else if (identifier.system === "https://euclinicaltrials.eu/clinicalTrialId") {
                        startingIdentifierSet.euCTnumber = identifier.value;
                    } else if (identifier.type?.text === "Sponsor Protocol Identifier" || identifier.type?.coding?.[0]?.code === "C132351") {
                        startingIdentifierSet.sponsorid = identifier.value;
                        if (identifier.assigner?.display) {
                            startingIdentifierSet.sponsorname = identifier.assigner.display;
                        }
                    } else if (identifier.type?.text === "FDA IND Number" || identifier.type?.coding?.[0]?.code === "C218685") {
                        startingIdentifierSet.fdaINDnumber = identifier.value;
                    } else if (identifier.type?.text === "IDE Number" || identifier.type?.coding?.[0]?.code === "C218686") {
                        startingIdentifierSet.ideNumber = identifier.value;
                    } else if (identifier.type?.text === "jRCT Number" || identifier.type?.coding?.[0]?.code === "C218687") {
                        startingIdentifierSet.jRCTnumber = identifier.value;
                    } else if (identifier.type?.text === "NMPA IND Number" || identifier.type?.coding?.[0]?.code === "C218688") {
                        startingIdentifierSet.nmpaINDnumber = identifier.value;
                    } else if (identifier.type?.text === "WHO/UTN Number" || identifier.type?.coding?.[0]?.code === "C218689") {
                        startingIdentifierSet.whoUTNnumber = identifier.value;
                    } else {
                        startingIdentifierSet.otherIdentifiers.push(identifier);
                    }
                }
            }
            startingIdentifierSet.loaded = true;
            setIdentifiersState(startingIdentifierSet);
            setStartingIdentifiersState(startingIdentifierSet);
        }
    }, [m11ResearchStudyResource]);

    return <div>
        <div className="h3Style h3EntryLabel" style={{ marginBottom: "0px" }}>
            Enter Identifiers for M11ResearchStudy.<span> This will be used for the M11 Report Title Page.</span>
        </div>
        {identifiersState.loaded && <>
            <div className="h4Style h4EntryLabel" style={{ marginBottom: "0px" }}>
                <span>Enter the </span>Sponsor Protocol Identifier<span>.</span>
            </div>
            <div style={{ maxWidth: "600px" }}>
                <StringEntry elementName='sponsorid'
                    required={true}
                    fieldLabel='Sponsor Protocol Identifier'
                    startingValue={identifiersState.sponsorid}
                    setResourceState={setIdentifiersState} />
                <StringEntry elementName='sponsorname'
                    fieldLabel='Sponsor Name'
                    startingValue={identifiersState.sponsorname}
                    setResourceState={setIdentifiersState} />
            </div>
            <div className="h4Style h4EntryLabel" style={{ marginBottom: "0px" }}>
                <span>If the protocol is posted to ClinicalTrials.gov, enter the </span>NCTID<span> (NCTxxxxxxxx).</span>
            </div>
            <div style={{ maxWidth: "600px" }}>
                <StringEntry elementName='nctid'
                    fieldLabel='ClinicalTrials.gov NCTID (if applicable)'
                    startingValue={identifiersState.nctid}
                    setResourceState={setIdentifiersState} />
            </div>
            <div className="h4Style h4EntryLabel" style={{ marginBottom: "0px" }}>
                <span>If the protocol is posted to EU CTIS, enter the </span>CTIS ID<span> (yyyy-5xxxxx-xx).</span>
            </div>
            <div style={{ maxWidth: "600px" }}>
                <StringEntry elementName='euCTnumber'
                    fieldLabel='EU CT Number (EU CTIS clinicalTrialId, if applicable)'
                    startingValue={identifiersState.euCTnumber}
                    setResourceState={setIdentifiersState} />
            </div>
            <div className="h4Style h4EntryLabel" style={{ marginBottom: "0px" }}>
                <span>Enter any of these other identifiers if applicable.</span>
            </div>
            <div style={{ maxWidth: "600px" }}>
                <StringEntry elementName='fdaINDnumber'
                    fieldLabel='FDA IND Number (if applicable)'
                    startingValue={identifiersState.fdaINDnumber}
                    setResourceState={setIdentifiersState} />
                <StringEntry elementName='ideNumber'
                    fieldLabel='IDE Number (if applicable)'
                    startingValue={identifiersState.ideNumber}
                    setResourceState={setIdentifiersState} />
                <StringEntry elementName='jRCTnumber'
                    fieldLabel='jRCT Number (if applicable)'
                    startingValue={identifiersState.jRCTnumber}
                    setResourceState={setIdentifiersState} />
                <StringEntry elementName='nmpaINDnumber'
                    fieldLabel='NMPA IND Number (if applicable)'
                    startingValue={identifiersState.nmpaINDnumber}
                    setResourceState={setIdentifiersState} />
                <StringEntry elementName='whoUTNnumber'
                    fieldLabel='WHO/UTN Number (Uxxxx-xxxx-xxxx, if applicable)'
                    startingValue={identifiersState.whoUTNnumber}
                    setResourceState={setIdentifiersState} />
            </div>
            <div className="h4Style h4EntryLabel" style={{ marginBottom: "0px" }}>
                <span>Enter any other identifiers you want to appear on the M11 Report Title Page.</span>
            </div>
            {!jsonEquals(identifiersState, {
                "sponsorid": "", "sponsorname": "",
                "euCTnumber": "", "fdaINDnumber": "", "ideNumber": "",
                "jRCTnumber": "", "nctid": "", "nmpaINDnumber": "",
                "whoUTNnumber": "", "otherIdentifiers": []
            }) &&
                <ArrayEntry datatype="Identifier" elementName='otherIdentifiers'
                    fieldLabel='Other Identifier' deletable={true} deletableArray={true}
                    startCollapsed={true} startEmptyArrayClosed={true} doNotShowFieldLabel={true}
                    startingValue={identifiersState.otherIdentifiers}
                    setResourceState={setIdentifiersState} />}
        </>}
    </div>
});

const M11Step002IdentifiersHelperText = <div>
    <h3>Enter Identifiers for M11ResearchStudy</h3>
    <div style={{ textAlign: "left" }}>
        <p>The Sponsor Protocol Identifier is required. It is a unique alphanumeric identifier for the trial, designated by the sponsor.</p>
        <p>Include all identifiers that are applicable for the trial and available at the time of protocol or amendment finalisation.</p>
        <p>For the EU CT Number (EU CTIS clinicalTrialId), the format should be yyyy-5xxxxx-xx with YYYY corresponding to a year e.g. 2024 and x being an integer</p>
        <p>Leave blank prompts for identifiers not available at the time of document finalisation.</p>
        <p>Add “other identifiers” if additional identifiers are needed.</p>
        <p>Click the 'Save All Changes' button.</p>
    </div>
</div>

// eslint-disable-next-line no-empty-pattern
const M11Step002ValidationCheck = ({ }) => {

    const apiToken = useSelector(selectCurrentUserApiToken);
    const m11ResearchStudyFoi = useSelector(selectM11ResearchStudyFoi);

    const [validationCheckState, setValidationCheckState] = useState({});

    useEffect(() => {
        if (m11ResearchStudyFoi) {
            validateStep(apiToken, 2, m11ResearchStudyFoi,
                setValidationCheckState)
        }
    }, [m11ResearchStudyFoi]);

    return <div>{validationCheckState?.success && <div>
        <h4>Step 2 (Enter Identifiers for M11ResearchStudy) {validationCheckState.step02numberOfChecks} issues checked. {validationCheckState.step02numberPassed} passed. {validationCheckState.step02numberFailed} failed.</h4>
        <p>Sponsor Protocol Identifier has data stored in M11ResearchStudy Resource: {validationCheckState.resultCheckSponsorProtocolIdentifier}</p>
        <p>Sponsor Name has data stored in M11ResearchStudy Resource: {validationCheckState.resultCheckSponsorName}</p>
        <p>If NCTID is present, it follows format of NCTxxxxxxxx where x is a digit: {validationCheckState.resultCheckNctid}</p>
        <p>If CTIS ID is present, it follows format of yyyy-5xxxxx-xx with YYYY corresponding to a year e.g. 2024 and x being an integer: {validationCheckState.resultCheckCtisid}</p>
        <p>If WHO/UTN Number is present, it follows format of Uxxxx-xxxx-xxxx where x is a digit: {validationCheckState.resultCheckWhoNumber}</p>
    </div>}  </div>
}

export default M11Step002Identifiers;
export { M11Step002IdentifiersHelperText, M11Step002ValidationCheck };